import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// UI strings only: card text (names, descriptions, moves) comes localized from the API.
//
// One namespace per area, one file per language: src/i18n/locales/<lang>/<area>.json. Every file is picked
// up here, so a feature adds strings without touching shared files. "common" is the default namespace.

export const LANGUAGES = ["en", "fr"] as const;
export type Language = (typeof LANGUAGES)[number];

const STORAGE_KEY = "hm.lang";

// Only "common" ships with the app: what the frame shows before any screen (loading, the error and offline
// states, the update prompt). Every other namespace loads when a screen first asks for it (useTranslation
// suspends meanwhile), so the first load carries no screen's strings.
const bundled = import.meta.glob<Record<string, unknown>>("./locales/*/common.json", {
  eager: true,
  import: "default",
});
const lazy = import.meta.glob<Record<string, unknown>>(["./locales/*/*.json", "!./locales/*/common.json"], {
  import: "default",
});

/**
 * Loaded with every screen, alongside its code (`page()` in src/app/feature.ts): "shared" holds the strings
 * of the shared components (navigation, the card, game values, demo notices) and "moments" the moments kit's,
 * which any screen can play. Keys missing from a namespace are looked up in "shared" (`fallbackNS`), so the
 * components still read them as `t("card.…")`.
 */
export const SCREEN_NAMESPACES = ["shared", "moments"] as const;

const resources: Record<string, Record<string, Record<string, unknown>>> = {};
for (const [path, body] of Object.entries(bundled)) {
  const [, language = "", namespace = ""] = /\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path) ?? [];
  (resources[language] ??= {})[namespace] = body;
}

/** Every namespace present in the catalogs. */
export const NAMESPACES = [
  "common",
  ...new Set(Object.keys(lazy).map((path) => /\/([^/]+)\.json$/.exec(path)?.[1] ?? "")),
].filter(Boolean);

/** Loads a namespace's catalog file on demand (an i18next backend). */
const lazyBackend = {
  type: "backend" as const,
  init: () => undefined,
  read(
    language: string,
    namespace: string,
    callback: (error: unknown, data: Record<string, unknown> | null) => void,
  ) {
    const load = lazy[`./locales/${language}/${namespace}.json`];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (data) => {
        callback(null, data);
      },
      (error: unknown) => {
        callback(error, null);
      },
    );
  },
};

function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && (LANGUAGES as readonly string[]).includes(value);
}

function stored(): Language | undefined {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isLanguage(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/** The saved choice, else the browser's first supported language, else English. */
export function detectLanguage(): Language {
  const saved = stored();
  if (saved) return saved;
  for (const tag of navigator.languages) {
    const base = tag.slice(0, 2).toLowerCase();
    if (isLanguage(base)) return base;
  }
  return "en";
}

export async function setLanguage(language: Language) {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Private mode or blocked storage: the choice lasts for this visit only.
  }
  await i18n.changeLanguage(language);
}

i18n.on("languageChanged", (language) => {
  document.documentElement.lang = language;
});

void i18n
  .use(lazyBackend)
  .use(initReactI18next)
  .init({
    resources,
    partialBundledLanguages: true,
    ns: ["common"],
    defaultNS: "common",
    fallbackNS: "shared",
    lng: detectLanguage(),
    fallbackLng: "en",
    supportedLngs: LANGUAGES,
    interpolation: { escapeValue: false },
    returnNull: false,
  });

export default i18n;
